// Order Email Service for The Frosting Room
// Handles order confirmation emails and other order-related email communications

import { sendEmail } from './customEmailService';
import { EmailData } from './emailService';
import { Order, OrderItem } from '../types';
import { formatFulfillmentWindow, getOrderFulfillmentTimes } from '../constants/fulfillmentWindows';

export interface OrderEmailData {
  order: Order;
  templateId: string;
  businessEmails: string[];
}

export interface OrderEmailResponse {
  success: boolean;
  messageId?: string;
  error?: string;
}

/**
 * Create order confirmation email data
 */
export const createOrderConfirmationEmail = async (order: Order): Promise<EmailData> => {
  try {
    // Format order date
    const orderDate = order.createdAt.toLocaleDateString('en-IE', {
      timeZone: 'Europe/Dublin',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Generate order items HTML
    const orderItemsHtml = generateOrderItemsHtml(order.items);

    // Generate delivery info HTML
    const deliveryInfoHtml = generateDeliveryInfoHtml(order);

    // Create email content
    const htmlContent = generateOrderConfirmationHtml({
      orderNumber: order.orderNumber,
      orderDate,
      customerName: `${order.customer.firstName} ${order.customer.lastName}`,
      customerEmail: order.customer.email,
      customerPhone: order.customer.phone || 'Not provided',
      orderItems: orderItemsHtml,
      subtotal: order.subtotal.toFixed(2),
      vatPercentage: order.vatPercentage.toString(),
      vatAmount: order.vat.toFixed(2),
      shipping: order.shipping.toFixed(2),
      total: order.total.toFixed(2),
      deliveryOption: order.deliveryOption === 'delivery' ? 'Delivery' : 'Collection',
      deliveryInfo: deliveryInfoHtml,
      paymentStatus: 'Confirmed',
      paymentMethod: 'Card Payment',
      paymentId: order.payment.id
    });

    // Create plain text version
    const textContent = generateOrderConfirmationText(order);

    return {
      to: [order.customer.email],
      cc: ['info@thefrostingroom.com'],
      bcc: ['thefrostingroom@icloud.com'],
      subject: `Order Confirmation - ${order.orderNumber} - The Frosting Room`,
      html: htmlContent,
      text: textContent,
      replyTo: 'thefrostingroom@icloud.com'
    };
  } catch (error) {
    console.error('Error creating order confirmation email:', error);
    throw new Error('Failed to create order confirmation email');
  }
};

/**
 * Send order confirmation email using Firebase Functions backend
 */
export const sendOrderConfirmationEmail = async (order: Order): Promise<OrderEmailResponse> => {
  try {
    console.log('📧 Sending order confirmation email for order:', order.orderNumber);
    
    // Use the existing helper to create email data
    const emailData = await createOrderConfirmationEmail(order);
    
    // Send email using Firebase Functions backend
    const emailResult = await sendEmail(emailData);
    
    if (emailResult.success) {
      console.log('✅ Order confirmation email sent successfully via Firebase Functions');
      return {
        success: true,
        messageId: emailResult.messageId
      };
    } else {
      console.error('❌ Failed to send order confirmation email:', emailResult.error);
      return {
        success: false,
        error: emailResult.error || 'Email sending failed'
      };
    }
    
  } catch (error) {
    console.error('❌ Error sending order confirmation email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

/**
 * Generate HTML for order items
 */
const generateOrderItemsHtml = (items: OrderItem[]): string => {
  return items.map(item => {
    const customizations = [];
    
    if (item.size) customizations.push(`Size: ${item.size}`);
    if (item.flavors && item.flavors.length > 0) customizations.push(`Flavors: ${item.flavors.join(', ')}`);
    if (item.shape) customizations.push(`Shape: ${item.shape}`);
    if (item.layers) customizations.push(`Layers: ${item.layers}`);
    if (item.filling) customizations.push(`Filling: ${item.filling}`);
    if (item.icing) customizations.push(`Icing: ${item.icing}`);
    if (item.baseColor) customizations.push(`Base Color: ${item.baseColor}`);
    if (item.pipingColor) customizations.push(`Piping Color: ${item.pipingColor}`);
    if (item.addOns && item.addOns.length > 0) customizations.push(`Add-ons: ${item.addOns.join(', ')}`);
    if (item.matchingCupcakes && item.matchingCupcakes.length > 0) customizations.push(`Matching Cupcakes: ${item.matchingCupcakes.join(', ')}`);
    if (item.hasImageOnCake) customizations.push('Image on Cake: Yes');
    if (item.hasSampleImage) customizations.push('Sample Image Provided: Yes');
    if (item.specialInstructions) customizations.push(`Special Instructions: ${item.specialInstructions}`);
    
    return `
      <div class="order-item">
        <div class="item-details">
          <div class="item-name">${item.name} (Qty: ${item.quantity})</div>
          <div class="item-customizations">${customizations.join(' • ')}</div>
        </div>
        <div class="item-price">€${item.totalPrice.toFixed(2)}</div>
      </div>
    `;
  }).join('');
};

/**
 * Generate plain text for order items (for EmailJS)
 */
const generateOrderItemsText = (items: OrderItem[]): string => {
  return items.map((item, index) => {
    const customizations = [];
    
    if (item.size) customizations.push(`Size: ${item.size}`);
    if (item.flavors && item.flavors.length > 0) customizations.push(`Flavors: ${item.flavors.join(', ')}`);
    if (item.shape) customizations.push(`Shape: ${item.shape}`);
    if (item.layers) customizations.push(`Layers: ${item.layers}`);
    if (item.filling) customizations.push(`Filling: ${item.filling}`);
    if (item.icing) customizations.push(`Icing: ${item.icing}`);
    if (item.baseColor) customizations.push(`Base Color: ${item.baseColor}`);
    if (item.pipingColor) customizations.push(`Piping Color: ${item.pipingColor}`);
    if (item.addOns && item.addOns.length > 0) customizations.push(`Add-ons: ${item.addOns.join(', ')}`);
    if (item.matchingCupcakes && item.matchingCupcakes.length > 0) customizations.push(`Matching Cupcakes: ${item.matchingCupcakes.join(', ')}`);
    if (item.hasImageOnCake) customizations.push('Image on Cake: Yes');
    if (item.hasSampleImage) customizations.push('Sample Image Provided: Yes');
    if (item.specialInstructions) customizations.push(`Special Instructions: ${item.specialInstructions}`);
    
    return `${index + 1}. ${item.name} (Qty: ${item.quantity})
   ${customizations.join(' • ')}
   Price: €${item.totalPrice.toFixed(2)}`;
  }).join('\n\n');
};

/**
 * Generate HTML for delivery information
 */
const generateDeliveryInfoHtml = (order: Order): string => {
  let html = '';
  
  // Show date for both delivery and collection
  if (order.deliveryDate) {
    const dateLabel = order.deliveryOption === 'delivery' ? 'Delivery Date' : 'Collection Date';
    const formattedDate = order.deliveryDate.toLocaleDateString('en-IE', {
      timeZone: 'Europe/Dublin',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    html += `
      <div class="field">
        <span class="label">${dateLabel}:</span>
        <div class="value">${formattedDate}</div>
      </div>
    `;
  }

  const { startTime, endTime } = getOrderFulfillmentTimes(order);
  const timeWindow = formatFulfillmentWindow(startTime, endTime);
  if (timeWindow) {
    const timeLabel = order.deliveryOption === 'delivery' ? 'Delivery Window' : 'Collection Window';
    html += `
      <div class="field">
        <span class="label">${timeLabel}:</span>
        <div class="value">${timeWindow}</div>
      </div>
    `;
  }
  
  if (order.deliveryOption === 'delivery' && order.deliveryAddress) {
    const address = order.deliveryAddress;
    const fullAddress = [
      address.line1,
      address.line2,
      address.city,
      address.state,
      address.postalCode,
      address.country
    ].filter(Boolean).join(', ');
    
    html += `
      <div class="field">
        <span class="label">Delivery Address:</span>
        <div class="value">${fullAddress}</div>
      </div>
    `;
  }
  
  if (order.deliveryInstructions) {
    html += `
      <div class="field">
        <span class="label">Delivery Instructions:</span>
        <div class="value">${order.deliveryInstructions}</div>
      </div>
    `;
  }
  
  return html;
};

/**
 * Generate complete order confirmation HTML
 */
const generateOrderConfirmationHtml = (data: {
  orderNumber: string;
  orderDate: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  orderItems: string;
  subtotal: string;
  vatPercentage: string;
  vatAmount: string;
  shipping: string;
  total: string;
  deliveryOption: string;
  deliveryInfo: string;
  paymentStatus: string;
  paymentMethod: string;
  paymentId: string;
}): string => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Order Confirmation - The Frosting Room</title>
      <style>
        /* The Frosting Room Brand Colors */
        :root {
          --primary-pink: #FFE5E5;
          --primary-pink-dark: #FFB3B3;
          --primary-pink-light: #FFF0F0;
          --secondary-pink: #FFD1DC;
          --dark-blue: #1A1A2E;
          --dark-blue-light: #2D2D44;
          --white: #FFFFFF;
          --gray-light: #F5F5F5;
          --gray: #E0E0E0;
          --purple: #8B5CF6;
          --font-primary: 'Roboto', sans-serif;
          --font-heading: 'Roboto', sans-serif;
        }
        
        body {
          font-family: var(--font-primary);
          line-height: 1.6;
          color: var(--dark-blue);
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
          background-color: var(--primary-pink-light);
        }
        
        .email-container {
          background: var(--white);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          border: 1px solid var(--gray);
        }
        
        .header {
          background: linear-gradient(135deg, var(--primary-pink) 0%, var(--secondary-pink) 100%);
          color: var(--dark-blue);
          padding: 30px 20px;
          text-align: center;
          position: relative;
        }
        
        .logo {
          width: 60px;
          height: 60px;
          margin: 0 auto 15px;
          background: var(--white);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .logo img {
          width: 40px;
          height: 40px;
          object-fit: contain;
        }
        
        .header h1 {
          margin: 0;
          font-family: var(--font-heading);
          font-size: 24px;
          font-weight: 600;
          color: var(--dark-blue);
        }
        
        .header p {
          margin: 5px 0 0 0;
          color: var(--dark-blue-light);
          font-weight: 500;
        }
        
        .content {
          padding: 30px 20px;
          background: var(--white);
        }
        
        .intro-text {
          color: var(--dark-blue);
          margin-bottom: 25px;
          font-size: 16px;
          text-align: center;
        }
        
        .order-info {
          background: var(--primary-pink-light);
          padding: 25px;
          border-radius: 8px;
          margin: 25px 0;
          border-left: 4px solid var(--primary-pink-dark);
          border: 1px solid var(--gray);
        }
        
        .field {
          margin-bottom: 20px;
        }
        
        .field:last-child {
          margin-bottom: 0;
        }
        
        .label {
          font-weight: 600;
          color: var(--dark-blue);
          display: block;
          margin-bottom: 8px;
          font-family: var(--font-heading);
        }
        
        .value {
          color: var(--dark-blue-light);
          padding: 12px 15px;
          background: var(--white);
          border-radius: 6px;
          border: 1px solid var(--gray);
          font-size: 15px;
        }
        
        .order-items {
          background: var(--white);
          border-radius: 8px;
          border: 1px solid var(--gray);
          margin: 20px 0;
          overflow: hidden;
        }
        
        .order-item {
          padding: 15px;
          border-bottom: 1px solid var(--gray);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .order-item:last-child {
          border-bottom: none;
        }
        
        .item-details {
          flex: 1;
        }
        
        .item-name {
          font-weight: 600;
          color: var(--dark-blue);
          margin-bottom: 5px;
        }
        
        .item-customizations {
          font-size: 14px;
          color: var(--dark-blue-light);
          margin-bottom: 5px;
        }
        
        .item-price {
          font-weight: 600;
          color: var(--dark-blue);
        }
        
        .order-summary {
          background: var(--gray-light);
          padding: 20px;
          border-radius: 8px;
          margin: 20px 0;
        }
        
        .summary-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;
          padding: 5px 0;
        }
        
        .summary-row:last-child {
          margin-bottom: 0;
          border-top: 2px solid var(--dark-blue);
          padding-top: 15px;
          font-weight: 600;
          font-size: 18px;
          color: var(--dark-blue);
        }
        
        .delivery-info {
          background: var(--primary-pink-light);
          padding: 20px;
          border-radius: 8px;
          margin: 20px 0;
          border-left: 4px solid var(--primary-pink-dark);
          border: 1px solid var(--gray);
        }
        
        .payment-info {
          background: var(--gray-light);
          padding: 15px;
          border-radius: 6px;
          color: var(--dark-blue-light);
          font-size: 14px;
          margin: 20px 0;
        }
        
        .footer {
          background: var(--dark-blue);
          color: var(--white);
          padding: 25px 20px;
          text-align: center;
          font-size: 13px;
        }
        
        .footer p {
          margin: 8px 0;
        }
        
        .footer .business-name {
          font-weight: 600;
          font-size: 16px;
          color: var(--primary-pink);
        }
        
        .footer .location {
          color: var(--primary-pink-light);
        }
        
        a {
          color: var(--primary-pink-dark);
          text-decoration: none;
          font-weight: 500;
        }
        
        a:hover {
          text-decoration: underline;
          color: var(--primary-pink);
        }
        
        .footer a {
          color: var(--primary-pink-light);
        }
        
        @media (max-width: 600px) {
          body {
            padding: 10px;
          }
          
          .header, .content, .footer {
            padding: 20px 15px;
          }
          
          .order-info, .delivery-info {
            padding: 20px 15px;
          }
          
          .header h1 {
            font-size: 20px;
          }
          
          .logo {
            width: 50px;
            height: 50px;
          }
          
          .logo img {
            width: 35px;
            height: 35px;
          }
          
          .order-item {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .item-price {
            margin-top: 10px;
            align-self: flex-end;
          }
        }
      </style>
    </head>
    <body>
      <div class="email-container">
        <div class="header">
          <div class="logo">
            <img src="https://the-frosting-room.web.app/images/logo/tfr.avif" alt="The Frosting Room Logo" />
          </div>
          <h1>Order Confirmation</h1>
          <p>The Frosting Room</p>
        </div>
        
        <div class="content">
          <p class="intro-text">Thank you for your order! Your payment has been processed successfully and your order is confirmed.</p>
          
          <div class="order-info">
            <div class="field">
              <span class="label">Order Number:</span>
              <div class="value">${data.orderNumber}</div>
            </div>
            
            <div class="field">
              <span class="label">Order Date:</span>
              <div class="value">${data.orderDate}</div>
            </div>
            
            <div class="field">
              <span class="label">Customer:</span>
              <div class="value">${data.customerName}</div>
            </div>
            
            <div class="field">
              <span class="label">Email:</span>
              <div class="value">
                <a href="mailto:${data.customerEmail}">${data.customerEmail}</a>
              </div>
            </div>
            
            <div class="field">
              <span class="label">Phone:</span>
              <div class="value">${data.customerPhone}</div>
            </div>
          </div>
          
          <div class="order-items">
            ${data.orderItems}
          </div>
          
          <div class="order-summary">
            <div class="summary-row">
              <span>Subtotal:</span>
              <span>€${data.subtotal}</span>
            </div>
            <div class="summary-row">
              <span>VAT (${data.vatPercentage}%):</span>
              <span>€${data.vatAmount}</span>
            </div>
            <div class="summary-row">
              <span>Shipping:</span>
              <span>€${data.shipping}</span>
            </div>
            <div class="summary-row">
              <span>Total:</span>
              <span>€${data.total}</span>
            </div>
          </div>
          
          <div class="delivery-info">
            <div class="field">
              <span class="label">Delivery Option:</span>
              <div class="value">${data.deliveryOption}</div>
            </div>
            
            ${data.deliveryInfo}
          </div>
          
          <div class="payment-info">
            <strong>Payment Status:</strong> ${data.paymentStatus}<br>
            <strong>Payment Method:</strong> ${data.paymentMethod}<br>
            <strong>Transaction ID:</strong> ${data.paymentId}
          </div>
        </div>
        
        <div class="footer">
          <p class="business-name">The Frosting Room</p>
          <p class="location">Dublin, Ireland</p>
          <p>If you have any questions about your order, please contact us at <a href="mailto:thefrostingroom@icloud.com">thefrostingroom@icloud.com</a></p>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Generate plain text version of order confirmation
 */
const generateOrderConfirmationText = (order: Order): string => {
  const orderDate = order.createdAt.toLocaleDateString('en-IE', {
    timeZone: 'Europe/Dublin',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  let text = `ORDER CONFIRMATION - The Frosting Room\n\n`;
  text += `Thank you for your order! Your payment has been processed successfully and your order is confirmed.\n\n`;
  
  text += `ORDER DETAILS:\n`;
  text += `Order Number: ${order.orderNumber}\n`;
  text += `Order Date: ${orderDate}\n`;
  text += `Customer: ${order.customer.firstName} ${order.customer.lastName}\n`;
  text += `Email: ${order.customer.email}\n`;
  text += `Phone: ${order.customer.phone || 'Not provided'}\n\n`;
  
  text += `ORDER ITEMS:\n`;
  order.items.forEach((item, index) => {
    text += `${index + 1}. ${item.name} (Qty: ${item.quantity})\n`;
    if (item.size) text += `   Size: ${item.size}\n`;
    if (item.flavors && item.flavors.length > 0) text += `   Flavors: ${item.flavors.join(', ')}\n`;
    if (item.shape) text += `   Shape: ${item.shape}\n`;
    if (item.layers) text += `   Layers: ${item.layers}\n`;
    if (item.filling) text += `   Filling: ${item.filling}\n`;
    if (item.icing) text += `   Icing: ${item.icing}\n`;
    if (item.baseColor) text += `   Base Color: ${item.baseColor}\n`;
    if (item.pipingColor) text += `   Piping Color: ${item.pipingColor}\n`;
    if (item.addOns && item.addOns.length > 0) text += `   Add-ons: ${item.addOns.join(', ')}\n`;
    if (item.matchingCupcakes && item.matchingCupcakes.length > 0) text += `   Matching Cupcakes: ${item.matchingCupcakes.join(', ')}\n`;
    if (item.hasImageOnCake) text += `   Image on Cake: Yes\n`;
    if (item.hasSampleImage) text += `   Sample Image Provided: Yes\n`;
    if (item.specialInstructions) text += `   Special Instructions: ${item.specialInstructions}\n`;
    text += `   Price: €${item.totalPrice.toFixed(2)}\n\n`;
  });
  
  text += `ORDER SUMMARY:\n`;
  text += `Subtotal: €${order.subtotal.toFixed(2)}\n`;
  text += `VAT (${order.vatPercentage}%): €${order.vat.toFixed(2)}\n`;
  text += `Shipping: €${order.shipping.toFixed(2)}\n`;
  text += `Total: €${order.total.toFixed(2)}\n\n`;
  
  text += `DELIVERY INFORMATION:\n`;
  text += `Delivery Option: ${order.deliveryOption === 'delivery' ? 'Delivery' : 'Collection'}\n`;
  
  // Show date for both delivery and collection
  if (order.deliveryDate) {
    const dateLabel = order.deliveryOption === 'delivery' ? 'Delivery Date' : 'Collection Date';
    const formattedDate = order.deliveryDate.toLocaleDateString('en-IE', {
      timeZone: 'Europe/Dublin',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    text += `${dateLabel}: ${formattedDate}\n`;
  }

  const { startTime, endTime } = getOrderFulfillmentTimes(order);
  const timeWindow = formatFulfillmentWindow(startTime, endTime);
  if (timeWindow) {
    const timeLabel = order.deliveryOption === 'delivery' ? 'Delivery Window' : 'Collection Window';
    text += `${timeLabel}: ${timeWindow}\n`;
  }
  
  if (order.deliveryOption === 'delivery' && order.deliveryAddress) {
    const address = order.deliveryAddress;
    const fullAddress = [
      address.line1,
      address.line2,
      address.city,
      address.state,
      address.postalCode,
      address.country
    ].filter(Boolean).join(', ');
    text += `Delivery Address: ${fullAddress}\n`;
  }
  
  if (order.deliveryInstructions) {
    text += `Delivery Instructions: ${order.deliveryInstructions}\n`;
  }
  
  text += `\nPAYMENT INFORMATION:\n`;
  text += `Payment Status: Confirmed\n`;
  text += `Payment Method: Card Payment\n`;
  text += `Transaction ID: ${order.payment.id}\n\n`;
  
  text += `If you have any questions about your order, please contact us at thefrostingroom@icloud.com\n\n`;
  text += `The Frosting Room\n`;
  text += `Dublin, Ireland\n`;
  
  return text;
};

/**
 * Create order processing email data
 */
export const createOrderProcessingEmail = async (order: Order): Promise<EmailData> => {
  try {
    // Format collection/delivery date
    const collectionDeliveryDate = order.deliveryDate 
      ? order.deliveryDate.toLocaleDateString('en-IE', {
          timeZone: 'Europe/Dublin',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      : 'To be confirmed';

    // Get collection/delivery times based on delivery option
    const startTime = order.deliveryOption === 'delivery' 
      ? order.deliveryStartTime 
      : order.collectionStartTime;
    const endTime = order.deliveryOption === 'delivery'
      ? order.deliveryEndTime
      : order.collectionEndTime;
    
    const timeWindow = startTime && endTime 
      ? `${startTime} - ${endTime}`
      : 'To be confirmed';

    // Format delivery address for delivery orders
    const deliveryAddressFormatted = order.deliveryOption === 'delivery' && order.deliveryAddress
      ? [
          order.deliveryAddress.line1,
          order.deliveryAddress.line2,
          order.deliveryAddress.city,
          order.deliveryAddress.state,
          order.deliveryAddress.postalCode,
          order.deliveryAddress.country
        ].filter(Boolean).join(', ')
      : undefined;

    // Create email content
    const htmlContent = generateOrderProcessingHtml({
      customerFirstName: order.customer.firstName,
      orderNumber: order.orderNumber,
      collectionDeliveryDate,
      timeWindow,
      deliveryOption: order.deliveryOption,
      deliveryAddress: deliveryAddressFormatted
    });

    // Create plain text version
    const textContent = generateOrderProcessingText({
      customerFirstName: order.customer.firstName,
      orderNumber: order.orderNumber,
      collectionDeliveryDate,
      timeWindow,
      deliveryOption: order.deliveryOption,
      deliveryAddress: deliveryAddressFormatted
    });

    return {
      to: [order.customer.email],
      cc: ['info@thefrostingroom.com'],
      bcc: ['thefrostingroom@icloud.com'],
      subject: `Your Order is Being Processed - ${order.orderNumber}`,
      html: htmlContent,
      text: textContent,
      replyTo: 'thefrostingroom@icloud.com'
    };
  } catch (error) {
    console.error('Error creating order processing email:', error);
    throw new Error('Failed to create order processing email');
  }
};

/**
 * Send order processing email using Firebase Functions backend
 */
export const sendOrderProcessingEmail = async (order: Order): Promise<OrderEmailResponse> => {
  try {
    console.log('📧 Sending order processing email for order:', order.orderNumber);
    
    // Validate that times are set
    const hasCollectionTimes = order.collectionStartTime && order.collectionEndTime;
    const hasDeliveryTimes = order.deliveryStartTime && order.deliveryEndTime;
    
    if (order.deliveryOption === 'delivery' && !hasDeliveryTimes) {
      throw new Error('Delivery times must be set before sending processing email');
    }
    
    if (order.deliveryOption === 'collection' && !hasCollectionTimes) {
      throw new Error('Collection times must be set before sending processing email');
    }
    
    // Use the helper to create email data
    const emailData = await createOrderProcessingEmail(order);
    
    // Send email using Firebase Functions backend
    const emailResult = await sendEmail(emailData);
    
    if (emailResult.success) {
      console.log('✅ Order processing email sent successfully via Firebase Functions');
      return {
        success: true,
        messageId: emailResult.messageId
      };
    } else {
      console.error('❌ Failed to send order processing email:', emailResult.error);
      return {
        success: false,
        error: emailResult.error || 'Email sending failed'
      };
    }
    
  } catch (error) {
    console.error('❌ Error sending order processing email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

/**
 * Generate HTML for order processing email
 */
const generateOrderProcessingHtml = (data: {
  customerFirstName: string;
  orderNumber: string;
  collectionDeliveryDate: string;
  timeWindow: string;
  deliveryOption: 'collection' | 'delivery';
  deliveryAddress?: string;
}): string => {
  const collectionAddress = 'Airton Plaza, Airton Road, Dublin 24, D24 WFW7';
  const contactNumber = '0899475387';
  const dateLabel = data.deliveryOption === 'delivery' ? 'Delivery Date' : 'Collection Date';
  const timeLabel = data.deliveryOption === 'delivery' ? 'Delivery Times' : 'Collection Times';
  const detailsIntro = data.deliveryOption === 'delivery' ? 'the delivery details below' : 'the collection details below';
  const addressLabel = data.deliveryOption === 'delivery' ? 'Delivery Address' : 'Collection Address';
  const addressValue = data.deliveryOption === 'delivery' && data.deliveryAddress ? data.deliveryAddress : collectionAddress;

  const policySection = data.deliveryOption === 'delivery'
    ? `
        <div style="margin: 20px 0;">
          <p><strong>Delivery Policy:</strong></p>
          <p>Please ensure you confirm a time within the delivery window that works best for you at least 24hrs before the delivery date. Please ensure someone is available to receive the order at the scheduled time. If you need to change the delivery time or have any issues, do let me know.</p>
        </div>`
    : `
        <div style="margin: 20px 0;">
          <p><strong>Collection Policy:</strong></p>
          <p>Please ensure you confirm a time within the collection window that works best for you at least 24hrs before collection date. Please be on time to your scheduled pick up time, if you are running late do let me know.</p>
        </div>`;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Your Order is Being Processed</title>
    </head>
    <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
      <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px;">
        <h2 style="color: #8b5cf6; margin-top: 0;">The Frosting Room</h2>
        
        <p>Dear ${data.customerFirstName},</p>
        
        <p>Thank you for your order from The Frosting Room. This email confirms that your order is being processed and ${detailsIntro}:</p>
        
        <div style="background-color: white; padding: 15px; border-radius: 5px; margin: 20px 0;">
          <p style="margin: 5px 0;"><strong>${dateLabel}:</strong> ${data.collectionDeliveryDate}</p>
          <p style="margin: 5px 0;"><strong>${addressLabel}:</strong> ${addressValue}</p>
          <p style="margin: 5px 0;"><strong>Contact Number:</strong> ${contactNumber}</p>
          <p style="margin: 5px 0;"><strong>${timeLabel}:</strong> ${data.timeWindow}</p>
        </div>
        ${policySection}
        
        <p>If you have any questions or need to update your details, please contact us as soon as possible.</p>
        
        <p style="margin-top: 30px;">
          Best Regards,<br>
          <strong>Gabrielle</strong><br>
          Executive Director<br>
          The Frosting Room
        </p>
      </div>
    </body>
    </html>
  `;
};

/**
 * Generate plain text for order processing email
 */
const generateOrderProcessingText = (data: {
  customerFirstName: string;
  orderNumber: string;
  collectionDeliveryDate: string;
  timeWindow: string;
  deliveryOption: 'collection' | 'delivery';
  deliveryAddress?: string;
}): string => {
  const collectionAddress = 'Airton Plaza, Airton Road, Dublin 24, D24 WFW7';
  const contactNumber = '0899475387';
  const dateLabel = data.deliveryOption === 'delivery' ? 'Delivery Date' : 'Collection Date';
  const timeLabel = data.deliveryOption === 'delivery' ? 'Delivery Times' : 'Collection Times';
  const detailsIntro = data.deliveryOption === 'delivery' ? 'the delivery details below' : 'the collection details below';
  const addressLabel = data.deliveryOption === 'delivery' ? 'Delivery Address' : 'Collection Address';
  const addressValue = data.deliveryOption === 'delivery' && data.deliveryAddress ? data.deliveryAddress : collectionAddress;

  const policyText = data.deliveryOption === 'delivery'
    ? 'Delivery Policy: Please ensure you confirm a time within the delivery window that works best for you at least 24hrs before the delivery date. Please ensure someone is available to receive the order at the scheduled time. If you need to change the delivery time or have any issues, do let me know.'
    : 'Collection Policy: Please ensure you confirm a time within the collection window that works best for you at least 24hrs before collection date. Please be on time to your scheduled pick up time, if you are running late do let me know.';

  let text = `Dear ${data.customerFirstName},\n\n`;
  text += `Thank you for your order from The Frosting Room. This email confirms that your order is being processed and ${detailsIntro}:\n\n`;
  text += `${dateLabel}: ${data.collectionDeliveryDate}\n`;
  text += `${addressLabel}: ${addressValue}\n`;
  text += `Contact Number: ${contactNumber}\n`;
  text += `${timeLabel}: ${data.timeWindow}\n\n`;
  text += `${policyText}\n\n`;
  text += `If you have any questions or need to update your details, please contact us as soon as possible.\n\n`;
  text += `Best Regards,\n`;
  text += `Gabrielle\n`;
  text += `Executive Director\n`;
  text += `The Frosting Room\n`;

  return text;
};

/**
 * Log email error for admin review
 */
export const logEmailError = (order: Order, error: string): void => {
  console.error('📧 EMAIL ERROR - Order Confirmation Failed:', {
    orderNumber: order.orderNumber,
    customerEmail: order.customer.email,
    error,
    timestamp: new Date().toISOString()
  });
  
  // In a production environment, you might want to:
  // 1. Send this to a logging service
  // 2. Store in a database for admin review
  // 3. Send an alert to admin email
  // 4. Create a support ticket
};
